import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { get } from '../utility/fetchWeb';

export const NotificationsContext = createContext();

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const getNotifications = useCallback(async (filter = "All") => {
    setLoading(true);
    try {
      const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
      if (!userId) return;

      let endpoint;
      let notificationData = [];

      if (filter === "All") {
        endpoint = `/Notifications/alluser/${userId}`;
       } else {
        // Try the specific endpoint first, fallback to all notifications and filter client-side
        endpoint = `/Notifications/alluser/${userId}`;
      }

      const response = await get(endpoint);
      if (response) {
        notificationData = response || [];

        // If the specific endpoint didn't work or returned empty, try filtering all notifications
        if (filter !== "All" && filter !== "Article" && (!notificationData || notificationData.length === 0)) {
          const allNotificationsResponse = await get(`/Notifications/alluser/${userId}`);
          if (allNotificationsResponse) {
            const allNotifications = allNotificationsResponse || [];
            notificationData = allNotifications.filter(n => 
              n.type?.toLowerCase() === filter.toLowerCase()
            );
          }
        }
        
        setNotifications(notificationData);
        
        // Calculate unread count
        const unread = notificationData.filter(n => !n.isRead).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Fallback: try to get all notifications and filter client-side
      if (filter !== "All") {
        try {
          const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
          const fallbackResponse = await get(`/Notifications/alluser/${userId}`);
          if (fallbackResponse) {
            const allNotifications = fallbackResponse || [];
            const filteredData = filter === "Article" 
              ? allNotifications.filter(n => n.type?.toLowerCase() === "article")
              : allNotifications.filter(n => n.type?.toLowerCase() === filter.toLowerCase());
            
            setNotifications(filteredData);
            const unread = filteredData.filter(n => !n.isRead).length;
            setUnreadCount(unread);
          }
        } catch (fallbackError) {
          console.error('Fallback filtering also failed:', fallbackError);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshNotifications = useCallback(() => {
    getNotifications();
  }, [getNotifications]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      // Update local state immediately for better UX
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, isRead: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

      // You can add an API call here to mark as read on the server
      // await put(`/Notifications/${notificationId}/mark-read`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  useEffect(() => {
    const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
    if (userId) {
      getNotifications();
    }
  }, [getNotifications]);

  return (
    <NotificationsContext.Provider value={{
      notifications,
      loading,
      unreadCount,
      getNotifications,
      refreshNotifications,
      markAsRead
    }}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
